/**
 * Rewrites the form_data of a source entry so it fits the schema it is going to
 * be used with.
 *
 * Using last year's entry as a template copies form_data across revisions. Keys
 * of fields the target schema no longer has stay in the payload invisibly, and
 * table rows keep the column positions of the source schema. Both are removed
 * here, so what is carried over is exactly what the target form can show.
 */
import { ITableAlignment } from "./inventoryItemExtractor";
import {
    alignTableRows,
    hydrateAlignedRows,
    parseTableRows,
    toAlignedColumns,
} from "./tableRowAligner";

export interface IMigrationReport {
    droppedFieldLabels: string[];
    tableAlignments: ITableAlignment[];
}

interface ISchemaField {
    type?: string;
    hashCode?: string;
    label?: string;
    subfields?: ISchemaField[];
    [key: string]: any;
}

export function parseSchema(raw: any): { fields?: ISchemaField[] } {
    if (raw && typeof raw === "object") {
        return raw;
    }
    try {
        return JSON.parse(raw || "{}") || {};
    } catch (e) {
        return {};
    }
}

const flattenFields = (fields: ISchemaField[] = [], out: ISchemaField[] = []): ISchemaField[] => {
    (fields || []).forEach((field) => {
        out.push(field);
        flattenFields(field.subfields, out);
    });
    return out;
};

const indexByHashCode = (fields: ISchemaField[]): Map<string, ISchemaField> => {
    const index = new Map<string, ISchemaField>();
    flattenFields(fields).forEach((field) => {
        if (field.hashCode) {
            index.set(field.hashCode, field);
        }
    });
    return index;
};

/**
 * @param formData - form_data of the source entry
 * @param rawTargetSchema - form_data of the revision the new entry will use
 * @param rawSourceSchema - form_data of the revision the source entry was filled with
 */
export function remapFormDataToSchema(
    formData: any,
    rawTargetSchema: any,
    rawSourceSchema: any = null
): { formData: any; report: IMigrationReport } {
    const report: IMigrationReport = {
        droppedFieldLabels: [],
        tableAlignments: [],
    };

    if (!formData || typeof formData !== "object") {
        return { formData: formData || {}, report };
    }

    const targetByHashCode = indexByHashCode(parseSchema(rawTargetSchema).fields || []);
    const sourceByHashCode = indexByHashCode(parseSchema(rawSourceSchema).fields || []);

    const result: any = {};

    Object.keys(formData).forEach((key) => {
        const baseHashCode = key.split("__")[0];
        const targetField = targetByHashCode.get(baseHashCode);

        if (!targetField) {
            const sourceField = sourceByHashCode.get(baseHashCode);
            const droppedLabel = sourceField?.label || baseHashCode;
            if (report.droppedFieldLabels.indexOf(droppedLabel) === -1) {
                report.droppedFieldLabels.push(droppedLabel);
            }
            return;
        }

        const rows = targetField.type === "TableField" ? parseTableRows(formData[key]) : null;
        if (!rows) {
            result[key] = formData[key];
            return;
        }

        const targetColumns = toAlignedColumns(targetField.subfields || []);
        const { rows: alignedRows, report: alignment } = alignTableRows(rows, targetColumns);
        const storedRows = hydrateAlignedRows(alignedRows, targetColumns);

        if (alignment.rowsShifted > 0 || alignment.unmatchedCells > 0) {
            report.tableAlignments.push({
                tableLabel: targetField.label || baseHashCode,
                report: alignment,
            });
        }

        result[key] = typeof formData[key] === "string" ? JSON.stringify(storedRows) : storedRows;
    });

    return { formData: result, report };
}
